import { compact } from 'lodash-es';
import { formatPaymentDetailsList, getStatusLabel } from '~/business-logic/invoiceLogic';
import { invoiceSummaryModel } from '~/models';
export var ClientInvoicesHeaderId = /*#__PURE__*/ function(ClientInvoicesHeaderId) {
    ClientInvoicesHeaderId["InvoiceId"] = "invoiceId";
    ClientInvoicesHeaderId["AgreementName"] = "agreementName";
    ClientInvoicesHeaderId["Status"] = "status";
    ClientInvoicesHeaderId["AdditionalInfo"] = "additionalInfo";
    ClientInvoicesHeaderId["IssueDate"] = "issueDate";
    ClientInvoicesHeaderId["DueDate"] = "dueDate";
    ClientInvoicesHeaderId["Total"] = "total";
    return ClientInvoicesHeaderId;
}({});
export function getClientInvoicesTableDefs(param) {
    let { texts, showAgreementName } = param;
    const entries = compact([
        {
            headerItem: {
                id: "invoiceId",
                value: texts.invoiceId
            },
            columnDef: {
                width: 140,
                sortable: true,
                mobile: {
                    value: {
                        weight: 'bold',
                        skin: 'standard'
                    }
                },
                bi: {
                    name: 'sort_invoice_id'
                }
            }
        },
        showAgreementName && {
            headerItem: {
                id: "agreementName",
                value: texts.agreementName
            },
            columnDef: {
                width: [
                    150,
                    200
                ],
                sortable: true,
                mobile: {},
                bi: {
                    name: 'sort_agreement_name'
                }
            }
        },
        {
            headerItem: {
                id: "status",
                value: texts.invoiceStatus
            },
            columnDef: {
                width: [
                    96,
                    140
                ],
                sortable: true,
                mobile: {
                    hide: true
                },
                bi: {
                    name: 'sort_invoice_status'
                }
            }
        },
        {
            headerItem: {
                id: "additionalInfo",
                value: texts.additionalInfo
            },
            columnDef: {
                width: [
                    160,
                    220
                ],
                sortable: false,
                mobile: {}
            }
        },
        {
            headerItem: {
                id: "issueDate",
                value: texts.issueDate
            },
            columnDef: {
                width: [
                    80,
                    100
                ],
                sortable: true,
                mobile: {
                    label: {
                        text: texts.issueDate
                    }
                },
                bi: {
                    name: 'sort_issue_date'
                }
            }
        },
        {
            headerItem: {
                id: "dueDate",
                value: texts.dueDate
            },
            columnDef: {
                width: [
                    80,
                    100
                ],
                sortable: true,
                mobile: {
                    hide: true
                },
                bi: {
                    name: 'sort_due_date'
                }
            }
        },
        {
            headerItem: {
                id: "total",
                value: texts.total
            },
            columnDef: {
                width: 84,
                sortable: true,
                hAlign: 'end',
                mobile: {
                    divider: true,
                    value: {
                        weight: 'bold',
                        skin: 'standard'
                    }
                },
                bi: {
                    name: 'sort_total'
                }
            }
        }
    ]);
    return {
        columnDefs: entries.map((e)=>e.columnDef),
        headerItems: entries.map((e)=>e.headerItem)
    };
}
export const EMPTY_FILTERS = {
    search: '',
    statuses: []
};
export function isAdditionalInfoMatchingSearch(invoice, search) {
    const text = invoice.paymentDetails.map((d)=>d.type).join(' ');
    return text.toLowerCase().includes(search);
}
export function applyClientInvoicesFilters(invoices, filters) {
    const search = filters.search.trim().toLowerCase();
    return invoices.filter((inv)=>{
        if (filters.statuses.length > 0 && !filters.statuses.includes(inv.displayStatusV2)) {
            return false;
        }
        if (filters.issuedOnSince && inv.rawIssuedOn < filters.issuedOnSince) {
            return false;
        }
        if (filters.issuedOnUntil && inv.rawIssuedOn > filters.issuedOnUntil) {
            return false;
        }
        if (search.length === 0) {
            return true;
        }
        if (inv.formattedNumber.toLowerCase().includes(search)) return true;
        if (invoiceSummaryModel.relationshipTitle(inv).toLowerCase().includes(search)) return true;
        if (isAdditionalInfoMatchingSearch(inv, search)) return true;
        return false;
    });
}
export function applyClientInvoicesSort(invoices, sort, sortIds) {
    if (sort == null) return invoices;
    const id = sortIds[sort.colIndex];
    if (id == null) return invoices;
    const direction = sort.sort === 'asc' ? 1 : -1;
    const copy = invoices.slice();
    copy.sort((a, b)=>{
        const cmp = compareInvoices(a, b, id);
        return cmp * direction;
    });
    return copy;
}
function compareInvoices(a, b, id) {
    switch(id){
        case "invoiceId":
            return a.formattedNumber.localeCompare(b.formattedNumber);
        case "agreementName":
            return invoiceSummaryModel.relationshipTitle(a).localeCompare(invoiceSummaryModel.relationshipTitle(b));
        case "status":
            return a.displayStatusV2.localeCompare(b.displayStatusV2);
        case "issueDate":
            return a.rawIssuedOn.getTime() - b.rawIssuedOn.getTime();
        case "dueDate":
            return a.rawDueDate.getTime() - b.rawDueDate.getTime();
        case "total":
            return a.rawTotalAmount.comparedTo(b.rawTotalAmount);
        default:
            return 0;
    }
}
export const DEFAULT_SORT = {
    colIndex: 4,
    sort: 'desc'
};
export function getAdditionalInfoText(invoice, texts) {
    return formatPaymentDetailsList(invoice.paymentDetails, texts.paymentDetailLabels, texts.labelOnDate);
}
export function getStatusText(invoice, texts) {
    return getStatusLabel(invoiceSummaryModel.displayStatus(invoice), texts.displayedStatusLabels);
}
export function parseDateRangeFilter(value) {
    if (value == null || typeof value !== 'object') return {};
    const range = value;
    const toDate = (raw)=>{
        if (raw == null || raw === '') return undefined;
        const ms = typeof raw === 'number' ? raw : Number(raw);
        return Number.isFinite(ms) && ms > 0 ? new Date(ms) : undefined;
    };
    return {
        since: toDate(range.startTime),
        until: toDate(range.endTime)
    };
}
export function distinctAgreementCount(invoices) {
    const set = new Set();
    invoices.forEach((inv)=>set.add(invoiceSummaryModel.relationshipId(inv)));
    return set.size;
}
export function isPaymentDetailMatching(detail) {
    return detail != null;
}
